import { Component } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '@services/api/api.service';
import { CommissionService } from '@services/api/commission.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-staff-commission',
  templateUrl: './staff-commission.component.html',
  styleUrls: ['./staff-commission.component.scss'],
})
export class StaffCommissionComponent {
  commissionForm: FormGroup;
  isDollar = false;
  isPercentage = true;
  tiered_commission = [];
  specific_commission = [];
  staff_id = null;
  staff_detail = null;
  isAdmin = false;
  commission_category = [
    { name: 'Listing', value: 'listing' },
    { name: 'Project', value: 'project' },
  ];

  public userRole = Crypto.decrypt(this.localStorageService.GetItem('role'));

  constructor(
    public Api: ApiService,
    private fb: FormBuilder,
    private toast: ToastrService,
    public lmService: LeadManagementService,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private commission_service: CommissionService
  ) {
    this.commissionForm = this.fb.group({
      commission_rate: null,
      commission_amount: null,
      overrides: false,
    });
  }

  /**
   * Initializes component properties and fetches necessary data.
   * - Sets `isAdmin` based on the user role.
   * - Retrieves `staff_id` from route parameters and fetches user details.
   * - Fetches agents, properties, projects, and agent commissions.
   */
  ngOnInit() {
    // Determine if the user is an admin or not based on their role
    this.isAdmin = this.userRole == 'master admin' || this.userRole == 'operations manager' ? false : true;

    // Subscribe to route parameters to retrieve the staff ID
    this.route.params.subscribe({
      next: params => {
        this.staff_id = params['id'] ? params['id'] : null; // Assign staff ID from route parameters
        this.getUser(this.staff_id); // Fetch user details using the retrieved staff ID
      },
    });
    // Fetch agents, properties, projects, and commissions for initialization
    this.lmService.getAgents();
    this.lmService.getProperties();
    this.lmService.GetProjectList();
    this.getAgentCommission();
  }

  // Fetch Staff details by id
  getUser(user_id = null) {
    if (user_id) {
      this.Api.get('/users/' + user_id + '?include=groups,offices,userTestimonials').subscribe({
        next: response => {
          this.staff_detail = response.data;
        },
      });
    }
  }

  updatePrice(value, param: string, index = 0) {
    if (param == 'commission_amount') {
      this.commissionForm.patchValue({
        commission_amount: value,
      });
    } else if (param == 'tiered_commission_amount') {
      this.tiered_commission[index].commission_amount = value;
    } else if (param == 'specific_commission_amount') {
      this.specific_commission[index].commission_amount = value;
    }
  }

  /**
   * Saves the agent's commission details by sending a PUT request.
   * - Gathers form data, including tiered and specific user commissions.
   * - Sends the data to the API for updating the agent's commission.
   * - Shows a success message on successful update.
   */
  saveCommission() {
    let payload = this.commissionForm.value;

    // Assign tiered and specific user commissions, ensuring arrays are not empty
    payload['tiered_commission'] = this.tiered_commission?.length > 0 ? this.tiered_commission : [];
    payload['specific_user_commission'] = this.specific_commission?.length > 0 ? this.specific_commission : [];
    payload['isPercentage'] = this.isPercentage;

    // Send PUT request to update the agent's commission
    this.commission_service.updateAgentCommission(this.staff_id, payload).subscribe({
      next: _ => {
        this.toast.success('Added successfully'); // Show success message
      },
    });
  }

  /**
   * Fetches the office commission details from the API.
   * - Updates the commission form with retrieved data.
   * - Determines if the commission is percentage-based.
   * - Filters specific user commissions for the given staff ID.
   */
  getOfficeCommission() {
    this.commission_service.getCommissionListByOfficeId().subscribe({
      next: response => {
        // Patch the commission rate and amount to the form
        this.commissionForm.patchValue({
          commission_rate: response['data']?.commission_rate ? response['data']?.commission_rate : null,
          commission_amount: response['data']?.commission_amount ? response['data']?.commission_amount : null,
        });

        // Determine commission type (percentage or fixed amount)
        this.isPercentage = response?.data?.isPercentage;

        // Assign tiered commission if available, otherwise set as an empty array
        this.tiered_commission = response['data']?.tiered_commission?.length > 0 ? response['data']?.tiered_commission : [];

        // Filter specific user commissions for the given staff ID
        let specific_user = [];
        if (response['data']?.specific_user_commission?.length > 0) {
          specific_user = response['data']?.specific_user_commission?.filter(ele => ele?.user_commission == this.staff_id);
        }
        this.specific_commission = specific_user;
      },
    });
  }

  /**
   * Fetches the agent's commission details from the API.
   * - Updates the form with retrieved data.
   * - Determines if the commission is percentage-based or dollar-based.
   * - Filters specific user commissions for the given staff ID.
   */
  getAgentCommission() {
    this.commission_service.getCommissionListByAgentId(this.staff_id).subscribe({
      next: response => {
        // Update form with received commission data
        this.commissionForm.patchValue(response?.data);

        // Assign tiered commission if available, otherwise set as an empty array
        this.tiered_commission = response['data']?.tiered_commission?.length > 0 ? response['data']?.tiered_commission : [];

        // Determine commission type and update boolean flags accordingly
        if (response?.data?.isPercentage == true) {
          this.isPercentage = true;
          this.isDollar = false;
        } else {
          this.isPercentage = false;
          this.isDollar = true;
        }

        // Filter specific user commissions for the given staff ID
        let specific_user = [];
        if (response['data']?.specific_user_commission?.length > 0) {
          specific_user = response['data']?.specific_user_commission?.filter(ele => ele?.user_commission == this.staff_id);
        }
        this.specific_commission = specific_user;
      },
    });
  }

  /**
   * Toggles the override functionality.
   * - If `overrides` is enabled, fetch office commission data.
   * - If disabled, resets tiered and specific user commissions.
   */
  overRide() {
    let dollar_val = this.commissionForm.controls['overrides'].value;

    if (dollar_val) {
      this.getOfficeCommission(); // Fetch office commission data if override is enabled
    } else {
      // Reset override field and clear commission data
      this.commissionForm.patchValue({
        overrides: false,
      });
      this.tiered_commission = [];
      this.specific_commission = [];
    }
  }

  /**
   * Toggles between percentage-based and dollar-based commission.
   * - Updates `isPercentage` and `isDollar` flags accordingly.
   * @param param - Determines whether to set commission as 'percentage' or fixed amount.
   */
  commissionToggle(param) {
    if (param == 'percentage') {
      this.isPercentage = true;
      this.isDollar = false;
    } else {
      this.isDollar = true;
      this.isPercentage = false;
    }
  }

  preventMinus(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'Subtract') {
      event.preventDefault(); // Block minus key
    }
  }

  preventNegativePaste(event: ClipboardEvent) {
    const pasteData = event.clipboardData?.getData('text');
    if (pasteData && parseFloat(pasteData) < 0) {
      event.preventDefault(); // Block pasting negative number
    }
  }
}
